import React, { useState, useEffect } from 'react';
import ScrollReactTable from 'components/shared/ScrollReactTable/ScrollReactTable';
import { Status, LOADING_CIRCLE } from 'common/utils/constants';
import classnames from 'classnames';

import './ConfigParamSettingsTable.css';
import Dropdown from 'components/shared/Dropdown/Dropdown';

const ConfigParamsSettingsTable = (props) => {
  const [paramData, setParamData] = useState([]);
  const [settingInputs, setSettingInputs] = useState();
  const [inputErrors, setInputErrors] = useState([]);
  const [editingRow, setEditingRow] = useState(-1);

  useEffect(() => {
    if (!props.data || !props.data.settings) return;
    setParamData([...props.data.settings]);
    setSettingInputs(Array.apply(null, Array(props.data.settings.length)).map(() => {}));
    setInputErrors(Array.apply(null, Array(props.data.settings.length)).map(() => {}));
    setEditingRow(-1);
  }, [props.data]);

  useEffect(() => {
    if (props.hideSaveBtn) {
      const newParamData = updateParamData();
      // props.setLocalSettings && props.setLocalSettings(newParamData);
      props.action && props.action(newParamData, inputErrors);
    }
  }, [settingInputs])

  const resetValues = () => {
    setEditingRow(-1);
    const emptyInputs = settingInputs.map((p) => '');
    setSettingInputs(emptyInputs);
  };

  const resetDefaultValues = () => {
    setEditingRow(-1);
    const defaults = paramData.map((p) => p.default);
    setSettingInputs(defaults);
    setInputErrors(Array.apply(null, Array(props.data.settings.length)).map(() => {}));
  };

  const updateParamData = () => {
    let newParams = paramData.map((p, i) => {
      let paramCopy = { ...p };
      if (settingInputs[i] !== undefined && settingInputs[i].length !== 0) {
        paramCopy.current = settingInputs[i];
      }
      return paramCopy;
    });
    return newParams;
  }

  const applyChanges = () => {
    const newParams = updateParamData();
    setParamData(newParams);
    resetValues();
    props.onApply && props.onApply(newParams, props.activeTableIndex);
  };

  const getDefaultParamsOverlay = () => {
    if (props.requestStatus === Status.PENDING) {
      return <div className="CreateSite_LoadingCircle_Overlay">{LOADING_CIRCLE}</div>;
    }
    if (props.requestStatus === Status.FAILURE) {
      return <div className="RequestErrorContainer">Fail to retrieve parameters. Please try again later.</div>;
    }

    return null;
  };

  const settingInputOnChange = (value, index, paramConfig) => {
    let parsed;
    let error;
    let min = paramConfig?.inputType?.inputOptions?.range?.min ?? Number.MIN_VALUE;
    let max = paramConfig?.inputType?.inputOptions?.range?.max ?? Number.MAX_VALUE;
    let step = paramConfig?.inputType?.inputOptions?.range?.step ?? 1;

    const inputType = paramConfig.dataType;
    if (inputType === 'integer') {
      parsed = parseInt(value);
      if (isNaN(parsed) && value !== '') return;
      if (isNaN(parsed)) {
        error = '';
        parsed = '';
      } else if (parsed < min) {
        error = `current value is smaller than the min value ${min}`;
      } else if (parsed > max) {
        error = `current value is larger than the max value ${max}`;
      } else if ((parsed - min) % step !== 0) {
        error = `current value does not match the increment step ${step}`;
      }
    } else if (inputType === 'float') {
      parsed = parseFloat(value);
      if (isNaN(parsed) && value !== '') return;
      if (isNaN(parsed)) {
        error = '';
        parsed = '';
      } else if (parsed < min) {
        error = `current value is smaller than min value ${min}`;
      } else if (parsed > max) {
        error = `current value is larger than max value ${max}`;
      }
    } else {
      parsed = value;
    }

    setEditingRow(index);
    setSettingInputs([...settingInputs.slice(0, index), parsed, ...settingInputs.slice(index + 1)]);
    setInputErrors([...inputErrors.slice(0, index), error, ...inputErrors.slice(index + 1)]);
  };

  const columns = (parent) => [
    {
      minWidth: props.widths?.settings_table_device_col_id || 200,
      // maxWidth: props.maxColWidth || 200,
      Header: (row) => {
        return (
          <div className="Param_Settings_Table_HeaderOuterDiv Param_Settings_Table_Text_LeftAlign">
            {props.itemName ?? 'ITEM'}
          </div>
        );
      },
      id: 'settings_table_device_col_id',
      accessor: (paramConfig) => paramConfig,
      Cell: (row) => {
        return (
          <div className="paramSettingsTable_CellContainer Param_Settings_Table_Text_LeftAlign">
            <div className="paramSettingsTable_Cell_Text">{row.value.displayName || 'Unknown'}</div>
          </div>
        );
      }
    },
    {
      minWidth: props.widths?.settings_table_curr_value_col_id || 200,
      // maxWidth: props.maxColWidth || 200,
      Header: (col) => {
        return <div className="Param_Settings_Table_HeaderOuterDiv">CURRENT VALUE</div>;
      },
      id: 'settings_table_curr_value_col_id',
      resizable: false,
      accessor: (paramConfig) => paramConfig,
      Cell: (row) => {
        return (
          <div className="paramSettingsTable_CellContainer">
            <div className="paramSettingsTable_Cell_Number">{row.value.current ?? row.value.default ?? 'Unknown'}</div>
            </div>
        );
      }
    },
    {
      minWidth: props.widths?.settings_table_default_value_col_id || 200,
      // maxWidth: props.maxColWidth || 200,
      Header: (col) => {
        return <div className="Param_Settings_Table_HeaderOuterDiv">DEFAULT VALUE</div>;
      },
      id: 'settings_table_default_value_col_id',
      resizable: false,
      accessor: (paramConfig) => paramConfig,
      Cell: (row) => {
        return <div className="paramSettingsTable_CellContainer">
          <div className="paramSettingsTable_Cell_Number">{row.value.default ?? 'Unknown'}</div>
          </div>;
      }
    },
    {
      minWidth: props.widths?.settings_table_new_value_col_id || 200,
      // maxWidth: props.maxColWidth || 200,
      Header: (col) => {
        return <div className="Param_Settings_Table_HeaderOuterDiv">NEW VALUE</div>;
      },
      id: 'settings_table_new_value_col_id',
      resizable: false,
      accessor: (paramConfig) => paramConfig,
      Cell: (row) => {
        const paramConfig = row.value;
        const index = row.index;
        let inputComponent;
        if (paramConfig.disabled) {
          inputComponent = (
            <div className="ParamSettingsTable_NewValueInput">
              <input className={'disabledInput'} type="number" value={''} readOnly={true}></input>
            </div>
          );
        } else if (paramConfig.inputType?.style === 'textbox') {
          inputComponent = (
            <div
              className={
                inputErrors[index]
                  ? 'ParamSettingsTable_NewValueInput_Invalid'
                  : 'ParamSettingsTable_NewValueInput_Valid'
              }
            >
              <input
                type={['integer', 'float'].includes(paramConfig.dataType) ? 'number' : 'text'}
                value={settingInputs[index]}
                onChange={(e) => {
                  settingInputOnChange(e.target.value, index, paramConfig);
                }}
                autoFocus={index === editingRow}
              ></input>
              {inputErrors[index] && <div className="ParamSettingsTable_InputErrrorMessage">{inputErrors[index]}</div>}
            </div>
          );
        } else if (paramConfig.inputType?.style === 'dropdown') {
          let options = [];
          const inputOptions = paramConfig.inputType?.inputOptions;
          if (inputOptions.itemSource === 'range') {
            let current = inputOptions.range.min;
            const step = inputOptions.range.step ?? 1;
            while (current <= inputOptions.range.max) {
              options.push(current);
              current += step;
            }
          } else if (inputOptions.itemSource === 'list') {
            options = inputOptions.list;
          }

          inputComponent = (
            <div className="paramSettings_dropdownContainer">
              <Dropdown
                title={settingInputs[index]}
                data={options}
                selectItem={(item) => {
                  setSettingInputs([...settingInputs.slice(0, index), item, ...settingInputs.slice(index + 1)]);
                  setEditingRow(-1);
                }}
              />
            </div>
          );
        }

        return (
          <div className="paramSettings_newValueContainer">
            {inputComponent}
            <div className="paramSettingsTable_UnitContainer">{row.value.unit}</div>
          </div>
        );
      }
    }
  ];

  return (
    <div className="paramSettingsContainer">
      <div className="ConfigParamSettingsTableContainer">
        {getDefaultParamsOverlay()}
        {props.requestStatus === Status.SUCCESS && (
          <ScrollReactTable
            showPagination={false}
            resizable={false}
            sortable={false}
            minRows={props.minRows || 2}
            noDataText={''}
            fetching={false}
            className={'Global_Table paramSettingsTable'}
            pageSize={paramData.length}
            data={paramData}
            columns={columns(paramData)}
            withFixedColumns={true}
          />
        )}
      </div>
      <div className="ParamSettingTable_BottomContainer">
        <button
          className={classnames({
            ParamSettingsTable_ValidResetButton: props.requestStatus === Status.SUCCESS,
            ParamSettingsTable_InvalidResetButton: props.requestStatus !== Status.SUCCESS
          })}
          onClick={resetDefaultValues}
        >
          Reset to default
        </button>
        {!props.hideSaveBtn && <button
          type="submit"
          className={classnames({
            ParamSettingsTable_ValidApplyButton:
              props.requestStatus === Status.SUCCESS && !inputErrors.some((error) => error),
            ParamSettingsTable_InvalidApplyButton:
              props.requestStatus !== Status.SUCCESS || inputErrors.some((error) => error)
          })}
          onClick={applyChanges}
        >
          Save
        </button>}
      </div>
    </div>
  );
};

export default ConfigParamsSettingsTable;
